import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AccreditationStandard, AccreditationStandardList, AWB } from '../../../_types/awb';
import { fetchAccreditationStandards } from '../../_data_sources/AccreditationStandards/useStandards';
import { getAccreditationVersion } from '../../_data_sources/AccreditationVersions/useAccreditationVersion';
import { useQueryClient, useQuery } from 'react-query';
import { isStandard, isSubStandard } from '../../../_helpers/awbHelper';
import { max } from 'lodash';
import { AppContext } from 'views/App/context';

interface StandardsContextType {
  version: AWB.Version | undefined;
  versionUpdateCounter: number;
  isLoading: boolean;
  navigateToStandard: (standardId: string) => void;
  clearStandards: () => void;
  fetchAllStandards: () => Promise<void>;
  searchStandards: (query: string) => { items: AWB.Standard[]; paths: string[][] };
  getNextPosition: (parentStandardId?: string) => number;
  getStandardType: (nodeId: string) => 'standard' | 'subStandard' | 'requirement' | undefined;
  expandedItems: string[];
  setExpandedItems: (items: string[] | ((prev: string[]) => string[])) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  toggleExpandedItem: (event: React.SyntheticEvent, itemId: string, isExpanded: boolean) => void;
}

const StandardsContext = createContext<StandardsContextType | undefined>(undefined);

export const useStandardsContext = () => {
  const context = useContext(StandardsContext);
  if (!context) {
    throw new Error('useStandardsContext must be used within a StandardsProvider');
  }
  return context;
};

interface StandardsProviderProps {
  children: React.ReactNode;
  accreditingBodyId: string;
  versionId: string;
  disabled?: boolean;
}

export const StandardsProvider: React.FC<StandardsProviderProps> = ({
  children,
  accreditingBodyId,
  versionId,
  disabled = false,
}) => {
  const versionRef = useRef<AWB.Version | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [versionUpdateCounter, setVersionUpdateCounter] = useState(0);
  const flatStandardsRef = useRef<AccreditationStandardList[]>([]);
  const queryClient = useQueryClient();
  const { currentOrganizationId, isFeatureEnabled } = useContext(AppContext);
  const useRUIData = isFeatureEnabled('awbRUIData') || isFeatureEnabled('awbPremiumDemo');

  // New state for expanded items and search
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    try {
      const stored = window.localStorage.getItem(`standards-expanded-${accreditingBodyId}-${versionId}`);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Save expanded items to localStorage when they change
  useEffect(() => {
    try {
      window.localStorage.setItem(
        `standards-expanded-${accreditingBodyId}-${versionId}`,
        JSON.stringify(expandedItems)
      );
    } catch (error) {
      // Silent fail for localStorage errors
    }
  }, [expandedItems, accreditingBodyId, versionId]);

  // Toggle expanded item helper
  const toggleExpandedItem = useCallback((event: React.SyntheticEvent, itemId: string, isExpanded: boolean) => {
    setExpandedItems((prev) => {
      const newExpanded = new Set(prev);
      if (isExpanded) {
        newExpanded.add(itemId);
      } else {
        newExpanded.delete(itemId);
      }
      return Array.from(newExpanded);
    });
  }, []);

  // Use react-query to fetch standards
  const { data: standards, isLoading: isStandardsLoading } = useQuery(
    ['accreditation_standards', currentOrganizationId, accreditingBodyId, versionId],
    () => fetchAccreditationStandards(currentOrganizationId, accreditingBodyId, versionId, useRUIData),
    {
      enabled: !disabled && !!versionId,
      onSuccess: async (data) => {
        if (Array.isArray(data)) {
          flatStandardsRef.current = data;
          const updatedVersion = await convertToHierarchy(data);
          versionRef.current = updatedVersion;
          setVersionUpdateCounter((prev) => prev + 1);
        }
      },
    }
  );

  // Update loading state based on react-query
  useEffect(() => {
    setIsLoading(isStandardsLoading);
  }, [isStandardsLoading]);

  const navigateToStandard = useCallback(
    (standardId: string) => {
      if (!standardId || standardId.includes('local-') || disabled) return;
      // The actual navigation should be handled by the component using this context
      // This function is just a placeholder to indicate the standard was selected
    },
    [disabled]
  );

  const clearStandards = useCallback(() => {
    versionRef.current = undefined;
    flatStandardsRef.current = [];
    setVersionUpdateCounter((prev) => prev + 1);
  }, []);

  const fetchAllStandards = useCallback(async () => {
    await queryClient.invalidateQueries(['accreditation_standards', accreditingBodyId, versionId]);
  }, [accreditingBodyId, versionId, queryClient]);

  // Convert flat standards into hierarchical structure
  const convertToHierarchy = useCallback(
    async (standards: AccreditationStandardList[]): Promise<AWB.Version> => {
      const standardsMap = new Map<string, AWB.Standard>();
      const rootStandards: AWB.Standard[] = [];

      // First pass: Create all standards
      for (const standard of standards) {
        const newStandard: AWB.Standard = {
          ...standard,
          draftContent: '',
          publishedContent: '',
          subStandards: [],
        };
        standardsMap.set(standard.id, newStandard);
      }

      // Second pass: Build hierarchy
      for (const standard of standards) {
        const newStandard = standardsMap.get(standard.id)!;
        if (standard.parentStandardId) {
          const parent = standardsMap.get(standard.parentStandardId);
          if (parent) {
            // Convert to SubStandard by adding requirements array
            const subStandard: AWB.SubStandard = {
              ...newStandard,
              requirements: [],
            };
            parent.subStandards.push(subStandard);
          }
        } else {
          rootStandards.push(newStandard);
        }
      }

      // Sort standards at each level by position and createdAt
      const sortStandards = (standards: (AWB.Standard | AWB.SubStandard)[]) => {
        standards.sort((a, b) => {
          // First sort by position, defaulting to 0 if not a number
          const posA = Math.max(0, Number(a.position) || 0);
          const posB = Math.max(0, Number(b.position) || 0);
          if (posA !== posB) {
            return posA - posB;
          }
          // If positions are equal, sort by createdAt
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });

        // Recursively sort subStandards
        standards.forEach((standard) => {
          if ('subStandards' in standard && standard.subStandards.length > 0) {
            sortStandards(standard.subStandards);
          }
        });
      };

      // Sort root standards and their descendants
      sortStandards(rootStandards);

      // Get version details from API
      const versionDetails = await getAccreditationVersion(
        currentOrganizationId,
        accreditingBodyId,
        versionId,
        useRUIData
      );

      const version: AWB.Version = {
        ...versionDetails,
        standards: rootStandards,
      };

      return version;
    },
    [versionId, accreditingBodyId, currentOrganizationId]
  );

  // Update version when data is available (including from cache)
  useEffect(() => {
    const updateVersion = async () => {
      if (standards && Array.isArray(standards) && !versionRef.current) {
        flatStandardsRef.current = standards;
        const updatedVersion = await convertToHierarchy(standards);
        versionRef.current = updatedVersion;
        setVersionUpdateCounter((prev) => prev + 1);
      }
    };

    updateVersion();
  }, [standards, convertToHierarchy]);

  // Search through hierarchical structure and return matching items with their paths
  const searchStandards = useCallback((query: string): { items: AWB.Standard[]; paths: string[][] } => {
    if (!versionRef.current || !query.trim()) {
      return { items: versionRef.current?.standards || [], paths: [] };
    }

    const lowerQuery = query.toLowerCase();
    const matchingNodeIds = new Set<string>();
    const paths: string[][] = [];

    // First pass: Find all matching nodes and their paths
    function findMatchingNodes(node: AWB.Node, currentPath: string[] = []) {
      const matchesQuery =
        (node.title && node.title.toLowerCase().includes(lowerQuery)) ||
        (node.heading && node.heading.toLowerCase().includes(lowerQuery));

      if (matchesQuery) {
        matchingNodeIds.add(node.id);
        paths.push([...currentPath, node.id]);
      }

      if ('subStandards' in node) {
        node.subStandards.forEach((child) => {
          findMatchingNodes(child, [...currentPath, node.id]);
        });
      }

      // Add check for requirements in SubStandards
      if ('requirements' in node) {
        (node as AWB.SubStandard).requirements.forEach((requirement) => {
          const requirementMatchesQuery =
            (requirement.title && requirement.title.toLowerCase().includes(lowerQuery)) ||
            (requirement.heading && requirement.heading.toLowerCase().includes(lowerQuery));

          if (requirementMatchesQuery) {
            matchingNodeIds.add(requirement.id);
            paths.push([...currentPath, node.id, requirement.id]);
          }
        });
      }
    }

    // Second pass: Build filtered tree with only matching nodes and their ancestors
    function buildFilteredTree(node: AWB.Node): AWB.Node | null {
      // If this node matches or has matching descendants, include it
      if (matchingNodeIds.has(node.id)) {
        if ('subStandards' in node) {
          const filteredChildren = node.subStandards
            .map((child) => buildFilteredTree(child))
            .filter((child): child is AWB.Node => child !== null);

          return {
            ...node,
            subStandards: filteredChildren as AWB.SubStandard[],
          };
        }
        return node;
      }

      if ('subStandards' in node) {
        const filteredChildren = node.subStandards
          .map((child) => buildFilteredTree(child))
          .filter((child): child is AWB.Node => child !== null);

        // If any descendants match, include this node with filtered children
        if (filteredChildren.length > 0) {
          return {
            ...node,
            subStandards: filteredChildren as AWB.SubStandard[],
          };
        }
      }

      // Check if any requirements match
      if ('requirements' in node) {
        const matchingRequirements = (node as AWB.SubStandard).requirements.filter((req) =>
          matchingNodeIds.has(req.id)
        );
        if (matchingRequirements.length > 0) {
          return {
            ...node,
            requirements: matchingRequirements,
          };
        }
      }

      return null;
    }

    versionRef.current.standards.forEach((root) => findMatchingNodes(root));

    const filteredStandards = versionRef.current.standards
      .map((root) => buildFilteredTree(root))
      .filter((root): root is AWB.Standard => root !== null);

    return { items: filteredStandards, paths };
  }, []);

  const getNextPosition = useCallback((parentStandardId?: string): number => {
    if (!flatStandardsRef.current.length) return 1;

    let existingStandards: AccreditationStandardList[] = [];
    if (!parentStandardId) {
      // For root level standards, filter standards with no parentStandardId
      existingStandards = flatStandardsRef.current.filter((s: AccreditationStandardList) => !s.parentStandardId);
    } else {
      // For substandards, filter standards with matching parentStandardId
      existingStandards = flatStandardsRef.current.filter(
        (s: AccreditationStandardList) => s.parentStandardId === parentStandardId
      );
    }

    // Get the highest position using lodash max, defaulting to 0 if no valid positions
    const highestPosition = max(existingStandards.map((s) => Math.max(0, Number(s.position) || 0))) || 0;
    return highestPosition + 1;
  }, []);

  const getStandardType = useCallback((nodeId: string): 'standard' | 'subStandard' | 'requirement' | undefined => {
    let result: 'standard' | 'subStandard' | 'requirement' | undefined = undefined;

    if (versionRef.current?.standards) {
      versionRef.current.standards.forEach((standard) => {
        if (standard.id === nodeId) {
          result = 'standard';
        } else if (standard.subStandards) {
          standard.subStandards.forEach((subStandard) => {
            if (subStandard.id === nodeId) {
              result = 'subStandard';
            } else if ('subStandards' in subStandard && subStandard.subStandards) {
              (subStandard.subStandards as AWB.Standard[]).forEach((nestedStandard) => {
                if (nestedStandard.id === nodeId) {
                  result = nestedStandard.standardCount === 0 ? 'requirement' : 'subStandard';
                }
              });
            }
          });
        }
      });
    }

    return result;
  }, []);

  const value = {
    version: versionRef.current,
    versionUpdateCounter,
    isLoading,
    navigateToStandard,
    clearStandards,
    fetchAllStandards,
    searchStandards,
    getNextPosition,
    getStandardType,
    expandedItems,
    setExpandedItems,
    searchQuery,
    setSearchQuery,
    toggleExpandedItem,
  };

  return <StandardsContext.Provider value={value}>{children}</StandardsContext.Provider>;
};
